import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, User, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuthContext } from '../contexts/AuthContext';
import { updateProfile } from '../api/auth';
import { useToast } from '../hooks/use-toast';

const EditProfile = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuthContext();
  const { toast } = useToast();
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.username) {
      setUsername(user.username);
    }
  }, [user]);

  const handleSave = async () => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "User not found. Please login again.",
        variant: "destructive"
      });
      return;
    }

    const trimmedUsername = username.trim();
    
    if (!trimmedUsername || trimmedUsername.length < 2) {
      toast({
        title: "Invalid Username",
        description: "Username must be at least 2 characters long.",
        variant: "destructive"
      });
      return;
    }

    if (trimmedUsername === user.username) {
      toast({
        title: "No Changes",
        description: "Username is the same as before.",
        variant: "default"
      });
      return;
    }

    try {
      setSaving(true);
      const response = await updateProfile({
        userId: user.id,
        username: trimmedUsername
      });

      if (response.success) {
        // Update user in context
        updateUser({ username: trimmedUsername });
        
        toast({
          title: "Success",
          description: "Profile updated successfully!",
        });

        // Navigate back after a short delay
        setTimeout(() => {
          navigate('/profile');
        }, 1000);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to update profile.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || error.message || "Failed to update profile. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#0A0F1F] via-[#1a1f3a] to-[#0A0F1F] text-white px-5 py-6 rounded-b-[2.5rem] shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#586BFF] rounded-full blur-3xl opacity-20 animate-pulse"></div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-6">
            <button 
              onClick={() => navigate('/profile')} 
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-semibold">Edit Profile</h1>
          </div>
        </div>
      </div>

      <div className="px-5 mt-6">
        <Card className="p-6">
          <div className="space-y-6">
            {/* Profile Avatar */}
            <div className="flex flex-col items-center mb-6">
              <div className="w-24 h-24 bg-gradient-to-br from-[#586BFF] to-[#9B62FF] rounded-full flex items-center justify-center text-white font-bold text-4xl shadow-lg mb-4">
                {(username || user?.username || user?.email || 'U').charAt(0).toUpperCase()}
              </div>
              <p className="text-sm text-gray-500">Profile Picture</p>
              <p className="text-xs text-gray-400 mt-1">Avatar is generated from your name</p>
            </div>

            {/* Name Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Enter your name"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10 h-12 text-base"
                  maxLength={50}
                  disabled={saving}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {username.length}/50 characters
              </p>
            </div>

            {/* Info Card */}
            <Card className="p-4 bg-blue-50 border-blue-200">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 text-blue-600 mt-0.5">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-900 mb-1">About Name Changes</p>
                  <p className="text-xs text-blue-700">
                    Your name is used across the app for identification. You can change it anytime, but make sure it's accurate for transactions.
                  </p>
                </div>
              </div>
            </Card>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={saving || !username.trim() || username.trim() === user?.username}
              className="w-full bg-gradient-to-r from-[#586BFF] to-[#9B62FF] hover:from-[#4a5ceb] hover:to-[#8a51eb] h-14 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>

            {/* Cancel Button */}
            <Button
              onClick={() => navigate('/profile')}
              variant="outline"
              className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 h-12"
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default EditProfile;

